import { FC, Fragment, useEffect, useId, useState } from 'react'
import type { MouseEvent } from 'react'
import Budget from '@/components/icons/map/filter/budget'
import { Box, Popover, Slider, Typography } from '@mui/material'
import { getCurrency, getOmitPrice } from '@/lib/currency'
import { statsigLog } from '@/lib/statsigAndGa'
import CloseCircle from '@/components/icons/map/filter/closeCircle'
import { BUDGET_EDGE, BUDGET_DEFAULT_LABEL } from './filterConfig'

export type budgetFilterType = { maxPrice: number | null; minPrice: number | null }

interface BudgetFilterProps {
  onApply: (budget: budgetFilterType) => void
  minPrice: number | null
  maxPrice: number | null
}

const calcLabel = (budget: budgetFilterType) => {
  const { minPrice, maxPrice } = budget
  if (!Number.isInteger(minPrice) && !Number.isInteger(maxPrice)) {
    return BUDGET_DEFAULT_LABEL
  }
  if (minPrice === BUDGET_EDGE.minPrice && maxPrice === BUDGET_EDGE.maxPrice) {
    return BUDGET_DEFAULT_LABEL
  }
  return `$${getOmitPrice(
    Number.isInteger(minPrice) ? minPrice : BUDGET_EDGE.minPrice
  )}-$${getOmitPrice(Number.isInteger(maxPrice) ? maxPrice : BUDGET_EDGE.maxPrice)}`
}

const BudgetFilter: FC<BudgetFilterProps> = props => {
  const { onApply, minPrice, maxPrice } = props
  const [open, setOpen] = useState(false)
  const popoverId = useId()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selected, setSelected] = useState<budgetFilterType>({
    maxPrice: BUDGET_EDGE.maxPrice,
    minPrice: BUDGET_EDGE.minPrice
  })
  const [label, setLabel] = useState(BUDGET_DEFAULT_LABEL)

  const onClickButton = (event: MouseEvent<HTMLButtonElement>) => {
    statsigLog('budget_filter_click')
    setAnchorEl(event.currentTarget)
    setOpen(true)
  }

  const onClickClear = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    const nullBudget = { minPrice: null, maxPrice: null }
    setLabel(calcLabel(nullBudget))
    onApply(nullBudget)
  }

  const onPopoverReset = () => {
    setSelected({
      minPrice: Number.isInteger(minPrice) ? minPrice : BUDGET_EDGE.minPrice,
      maxPrice: Number.isInteger(maxPrice) ? maxPrice : BUDGET_EDGE.maxPrice
    })
  }

  const onPopoverApply = () => {
    setOpen(false)
    setLabel(calcLabel(selected))
    // 如果还是最大最小值 则都置为null
    if (selected.minPrice === BUDGET_EDGE.minPrice && selected.maxPrice === BUDGET_EDGE.maxPrice) {
      onApply({ minPrice: null, maxPrice: null })
    } else {
      onApply(selected)
    }
  }

  useEffect(() => {
    setSelected({
      minPrice: Number.isInteger(minPrice) ? minPrice : BUDGET_EDGE.minPrice,
      maxPrice: Number.isInteger(maxPrice) ? maxPrice : BUDGET_EDGE.maxPrice
    })
    setLabel(calcLabel({ minPrice, maxPrice }))
  }, [minPrice, maxPrice])

  return (
    <>
      <Box
        aria-describedby={popoverId}
        component="button"
        onClick={onClickButton}
        className={`flex items-center gap-2 py-[6px] px-4 border border-gray-200 rounded-20 hover:bg-gray-100 ${
          label !== BUDGET_DEFAULT_LABEL ? 'border-primary bg-gray-100' : 'border-gray-200'
        }`}
      >
        <Budget className="w-4 h-4" />
        <Typography className="text-primary text-sm">{label}</Typography>
        {label !== BUDGET_DEFAULT_LABEL && (
          <CloseCircle className="w-4 h-4" onClick={onClickClear} />
        )}
      </Box>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setOpen(false)
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{
          paper: 'rounded-xl bg-white p-4 mt-4 shadow-[0_1px_7px_0_#242A2E1A]'
        }}
      >
        <Box component="div" className="flex flex-wrap items-center mt-5 gap-3 w-80 px-3">
          <Slider
            min={BUDGET_EDGE.minPrice}
            max={BUDGET_EDGE.maxPrice}
            step={100}
            value={[selected.minPrice, selected.maxPrice]}
            onChange={(_, values) => {
              setSelected({ minPrice: values[0], maxPrice: values[1] })
            }}
            classes={{
              root: 'h-[3px]',
              rail: 'bg-[#E0E2EF]',
              thumb: 'bg-white border-black border-2 rounded-full shadow-none',
              valueLabel: 'text-primary font-semibold p-0 bg-white before:hidden'
            }}
            valueLabelDisplay="on"
            valueLabelFormat={val => `$${getCurrency(val)}`}
          />
        </Box>
        <Box component="div" className="mt-4 flex justify-between items-end">
          <Typography
            onClick={onPopoverReset}
            className="text-primary font-semibold underline cursor-pointer"
          >
            Reset
          </Typography>
          <Box
            component="button"
            onClick={onPopoverApply}
            className="bg-brand text-white font-semibold rounded-xl py-2 px-6"
          >
            Apply
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default BudgetFilter
